import type { DeviceError } from '@/types/device';

/**
 * Error codes for device communication
 */
export const DeviceErrorCode = {
  // Connection errors (1xx)
  CONNECTION_FAILED: 100,
  CONNECTION_LOST: 101,
  CONNECTION_TIMEOUT: 102,
  DEVICE_NOT_FOUND: 103,
  PERMISSION_DENIED: 104,
  INTERFACE_CLAIM_FAILED: 105,

  // Communication errors (2xx)
  SEND_FAILED: 200,
  RECEIVE_FAILED: 201,
  RECEIVE_TIMEOUT: 202,
  DECODE_ERROR: 203,
  ACKNOWLEDGEMENT_MISMATCH: 204,

  // Device errors (3xx)
  DEVICE_BUSY: 300,
  DEVICE_ERROR: 301,
  ELECTRODE_NOT_DETECTED: 302,
  LOW_BATTERY: 303,
  HARDWARE_FAULT: 304,

  // Measurement errors (4xx)
  MEASUREMENT_TIMEOUT: 400,
  MEASUREMENT_FAILED: 401,
  MEASUREMENT_CANCELLED: 402,

  // Configuration errors (5xx)
  WEBUSB_NOT_SUPPORTED: 500,
  INVALID_PROFILE: 501,
  POINTS_LIMIT_EXCEEDED: 502,
  BLE_NOT_SUPPORTED: 503,
} as const;

export type DeviceErrorCode = (typeof DeviceErrorCode)[keyof typeof DeviceErrorCode];

/**
 * Create a DeviceError from a code and optional message
 */
export function createDeviceError(
  code: DeviceErrorCode | number,
  message?: string,
  recoverable = true,
  recoveryAction?: DeviceError['recoveryAction'],
  deviceErrorCode?: number
): DeviceError {
  const defaultMessages: Record<number, string> = {
    [DeviceErrorCode.CONNECTION_FAILED]: 'Failed to connect to device',
    [DeviceErrorCode.CONNECTION_LOST]: 'Connection to device lost',
    [DeviceErrorCode.CONNECTION_TIMEOUT]: 'Connection timed out',
    [DeviceErrorCode.DEVICE_NOT_FOUND]: 'No compatible device found',
    [DeviceErrorCode.PERMISSION_DENIED]: 'Permission to access device denied',
    [DeviceErrorCode.INTERFACE_CLAIM_FAILED]: 'Failed to claim USB interface',
    [DeviceErrorCode.SEND_FAILED]: 'Failed to send command to device',
    [DeviceErrorCode.RECEIVE_FAILED]: 'Failed to receive data from device',
    [DeviceErrorCode.RECEIVE_TIMEOUT]: 'Timed out waiting for device response',
    [DeviceErrorCode.DECODE_ERROR]: 'Failed to decode device response',
    [DeviceErrorCode.ACKNOWLEDGEMENT_MISMATCH]: 'Device acknowledgement mismatch - communication error detected',
    [DeviceErrorCode.DEVICE_BUSY]: 'Device is busy',
    [DeviceErrorCode.DEVICE_ERROR]: 'Device reported an error',
    [DeviceErrorCode.ELECTRODE_NOT_DETECTED]: 'Electrode not detected',
    [DeviceErrorCode.LOW_BATTERY]: 'Device battery is low',
    [DeviceErrorCode.HARDWARE_FAULT]: 'Device hardware fault detected',
    [DeviceErrorCode.MEASUREMENT_TIMEOUT]: 'Measurement timed out',
    [DeviceErrorCode.MEASUREMENT_FAILED]: 'Measurement failed',
    [DeviceErrorCode.MEASUREMENT_CANCELLED]: 'Measurement was cancelled',
    [DeviceErrorCode.WEBUSB_NOT_SUPPORTED]:
      'WebUSB is not supported in this browser',
    [DeviceErrorCode.BLE_NOT_SUPPORTED]:
      'Web Bluetooth is not supported in this browser',
    [DeviceErrorCode.INVALID_PROFILE]: 'Invalid measurement profile',
    [DeviceErrorCode.POINTS_LIMIT_EXCEEDED]: 'Measurement exceeds the device\'s points limit',
  };

  const defaultRecoveryActions: Partial<
    Record<number, DeviceError['recoveryAction']>
  > = {
    [DeviceErrorCode.CONNECTION_FAILED]: 'reconnect',
    [DeviceErrorCode.CONNECTION_LOST]: 'reconnect',
    [DeviceErrorCode.CONNECTION_TIMEOUT]: 'retry',
    [DeviceErrorCode.DEVICE_BUSY]: 'retry',
    [DeviceErrorCode.ELECTRODE_NOT_DETECTED]: 'check_electrode',
    [DeviceErrorCode.LOW_BATTERY]: undefined, // Warning, not blocking
    [DeviceErrorCode.HARDWARE_FAULT]: 'restart_device',
    [DeviceErrorCode.MEASUREMENT_TIMEOUT]: 'retry',
    [DeviceErrorCode.MEASUREMENT_FAILED]: 'retry',
    // POINTS_LIMIT_EXCEEDED intentionally has no entry here: none of the existing
    // recoveryAction values fit "reduce your sweep configuration," so it's left unset
    // (falls through to `undefined` via defaultRecoveryActions[code]).
  };

  return {
    code,
    message: message || defaultMessages[code] || `Unknown error (code: ${code})`,
    recoverable,
    recoveryAction: recoveryAction || defaultRecoveryActions[code],
    deviceErrorCode,
    timestamp: new Date(),
  };
}

/**
 * Check if a JavaScript error is a WebUSB security error
 */
export function isSecurityError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'SecurityError';
}

/**
 * Check if a JavaScript error is a WebUSB not found error
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotFoundError';
}

/**
 * Check if a JavaScript error is a USB transfer error
 */
export function isTransferError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NetworkError' || error.name === 'AbortError')
  );
}

/**
 * Classify a JavaScript error into a DeviceError
 */
export function classifyError(error: unknown): DeviceError {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'SecurityError':
        return createDeviceError(
          DeviceErrorCode.PERMISSION_DENIED,
          'User denied access to USB device',
          true,
          'reconnect'
        );
      case 'NotFoundError':
        return createDeviceError(
          DeviceErrorCode.DEVICE_NOT_FOUND,
          'No compatible device was selected',
          true,
          'reconnect'
        );
      case 'NetworkError':
        return createDeviceError(
          DeviceErrorCode.CONNECTION_LOST,
          'Device was disconnected',
          true,
          'reconnect'
        );
      case 'AbortError':
        return createDeviceError(
          DeviceErrorCode.MEASUREMENT_CANCELLED,
          'Operation was cancelled',
          true
        );
      default:
        return createDeviceError(
          DeviceErrorCode.DEVICE_ERROR,
          error.message,
          true,
          'retry'
        );
    }
  }

  if (error instanceof Error) {
    return createDeviceError(
      DeviceErrorCode.DEVICE_ERROR,
      error.message,
      true,
      'retry'
    );
  }

  return createDeviceError(
    DeviceErrorCode.DEVICE_ERROR,
    'An unknown error occurred',
    true,
    'retry'
  );
}
